import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { setUnlockedCookie } from '../../../tools/hooks/useCheckGatedCookie';
import { PrimaryLink } from '../../Common/Links';
import EmbeddedMarketoForm from '../../Common/MarketoForm/EmbeddedMarketoForm';
import { BLIND_FORM_ID, useLeadFormEffect } from '../../Common/MarketoForm/formEffects';
import { smoothScrollToRef } from '../../Common/ScrollTo';
import FormImage from './FormImage';
import * as styles from './GatedForm.module.scss';
import GatedSuccessMessage from './GatedSuccessMessage';

//const COOKIE = '_fs_axs';

const FormSection = ({ marketoFormId, onRendered, onFormSuccess, className, slug }) => {
  return (
    <EmbeddedMarketoForm
      onSuccess={(vals) => {
        onFormSuccess(vals, marketoFormId);
      }}
      onRendered={onRendered}
      className={className}
      slug={slug}
      formId={marketoFormId}
    />
  );
};

const GatedForm = ({
  title,
  successCopy,
  fileUrl,
  contentIsDownloadable,
  heroImage,
  marketoFormId,
  onSuccess,
  trackingPixelUrl,
  uniqueTrackingPixel,
  downloadCtaLabel,
  slug,
  className,
}) => {
  const [showFormSuccess, setShowFormSuccess] = useState(false); //useState(!!Cookies.get(cookieName));
  const [marketoForm, setMarketoForm] = useState(null);
  const [blindSubmitting, setBlindSubmitting] = useState(false);

  const [successMessage, setSuccessMessage] = useState(
    <div className={styles.success} data-analytics-section={'form-success-message'}>
      <h2>Thank You</h2>
      <p>Please check your email for the file.</p>
    </div>
  );

  const formRef = useRef(null);

  const { form, displayGate, lead, leadFailed } = useLeadFormEffect({
    marketoFormId,
  });

  let label = downloadCtaLabel || 'Download the paper';

  //useCheckGatedCookie(setShowFormSuccess, showFormSuccess);

  const isDownloadable = contentIsDownloadable?.[0] === 'Yes';
  const hasOverride = !!successCopy;

  useEffect(() => {
    setSuccessMessage(
      <GatedSuccessMessage
        successCopy={successCopy}
        label={label}
        isDownloadable={isDownloadable}
        hasOverride={hasOverride}
        fileUrl={fileUrl}
      />
    );
  }, [isDownloadable, hasOverride, fileUrl, successCopy, label]);

  const onFormSuccess = (formValues, formId) => {
    setShowFormSuccess(true);
    setUnlockedCookie(formId);
    smoothScrollToRef(formRef, 'smooth');
    onSuccess && onSuccess();
  };

  const onRendered = (formInstance) => {
    if (displayGate) return;
    setMarketoForm(formInstance);
    formInstance.setValues({
      Email: lead?.email,
      FirstName: lead?.firstName,
      LastName: lead?.lastName,
      LeadId: lead?.id,
    });
  };

  const onBlindSubmit = async () => {
    setBlindSubmitting(true);
    try {
      await marketoForm.submit();
      setShowFormSuccess(true);
    } catch (error) {
      if (typeof window !== 'undefined' && window.FS) {
        window.FS.event('Marketo Form', {
          type: 'Blind form submit failed',
          formID: BLIND_FORM_ID,
        });
      }
      console.log('Error submitting form:', error);
    } finally {
      setBlindSubmitting(false);
    }
  };

  return (
    <div className={styles.container} ref={formRef} data-analytics-component={'form-wrapper'}>
      <FormImage heroImage={heroImage} imgTitle={title} />
      {!showFormSuccess && (
        <>
          {!form && <span key="loading-spinner" className={styles.spinner} />}
          {!displayGate && form && (
            <div className={styles.access}>
              <PrimaryLink
                className={blindSubmitting ? styles.disabled : ''}
                isButton={true}
                label={'Access Content'}
                onClick={blindSubmitting ? () => {} : onBlindSubmit}
              />
            </div>
          )}
          {form && (
            <div className={!displayGate ? styles.hideForm : ''}>
              <FormSection
                marketoFormId={form}
                className={className}
                slug={slug}
                onFormSuccess={onFormSuccess}
                onRendered={onRendered}
              />
            </div>
          )}
        </>
      )}
      {showFormSuccess && (
        <div key="success-message">
          {successMessage}
          {trackingPixelUrl && <img src={trackingPixelUrl} height="1" width="1" style={{ display: 'none' }} alt="" />}
        </div>
      )}
      {showFormSuccess && uniqueTrackingPixel && (
        <img src={uniqueTrackingPixel} height="1" width="1" style={{ display: 'none' }} alt="" />
      )}
    </div>
  );
};

export const propTypes = {
  title: PropTypes.string,
  successCopy: PropTypes.object,
  fileUrl: PropTypes.string,
  contentIsDownloadable: PropTypes.array,
  heroImage: PropTypes.object,
  marketoFormId: PropTypes.string,
};

GatedForm.propTypes = {
  ...propTypes,
};

export default GatedForm;
